import { ReactNode } from 'react';

import { SystemProps } from '@sternum/styled-system';
import { hexToRgba } from '@sternum/web/utils';

import { TimePoint } from './TimePoint.component';
import { TimeSeparator } from './TimeSeparator';

import {
  Connector,
  DetailTitle,
  DetailValue,
  TimelineDetailsContainer,
  TimelineGraphStyled,
  TimelinePointContainer,
  TimelineRowItem,
} from './TimelineGraph.styles';

export interface TimelineGraphRenterTimePointArgs {
  timelineItem: TimelineGraphItemData;
}

export interface TimelineGraphRenterConnectorArgs {
  timelineItem: TimelineGraphItemData;
  timelineItemAfter: TimelineGraphItemData;
}

export interface TimelineGraphItemData {
  id: string;
  isSelected?: boolean;
  timePoint: ReactNode;
  timePointColor: string;
  details: ReactNode;
}

export interface TimelineGraphProps extends Omit<SystemProps, 'color'> {
  timelineData?: TimelineGraphItemData[];
  onClickTimePoint?: (timelineItem: TimelineGraphItemData) => unknown;

  renderTimePoint?: (args: TimelineGraphRenterTimePointArgs) => ReactNode;
  renderDetails?: (args: TimelineGraphRenterTimePointArgs) => ReactNode;
  renderConnector?: (args: TimelineGraphRenterConnectorArgs) => ReactNode;
}

export function TimelineGraph({
  timelineData = [],
  onClickTimePoint,
  renderTimePoint,
  renderDetails,
  renderConnector,
  ...props
}: TimelineGraphProps) {
  const renderTimePointLocal =
    renderTimePoint ??
    ((args: TimelineGraphRenterTimePointArgs) => {
      return (
        <TimePoint
          role="presentation"
          aria-label="timeline time point"
          key={args.timelineItem.id}
          color={args.timelineItem.timePointColor}
          isSelected={args.timelineItem.isSelected}
          onClick={() => onClickTimePoint?.(args.timelineItem)}
        >
          {args.timelineItem.timePoint}
        </TimePoint>
      );
    });

  const renderDetailsLocal =
    renderDetails ??
    ((args: TimelineGraphRenterTimePointArgs) => {
      return args.timelineItem.details;
    });

  const renderConnectorLocal =
    renderConnector ??
    ((args: TimelineGraphRenterConnectorArgs) => {
      const colorBefore = args.timelineItem.timePointColor;
      const colorAfter = args.timelineItemAfter.timePointColor;
      const colorBeforeParsed = hexToRgba.isValidHex(colorBefore) ? hexToRgba(colorBefore) : colorBefore;
      const colorAfterParsed = hexToRgba.isValidHex(colorAfter) ? hexToRgba(colorAfter) : colorAfter;
      const backgroundColor = `linear-gradient(0deg, ${colorAfterParsed} 0%, ${colorBeforeParsed} 100%)`;

      return <Connector aria-label="timeline connector" background={backgroundColor} />;
    });

  return (
    <TimelineGraphStyled {...props}>
      {timelineData.map((timelineDataItem, index) => (
        <TimelineRowItem role="presentation" aria-label="timeline row item" key={timelineDataItem.id}>
          <TimelinePointContainer>
            {index < timelineData?.length - 1 &&
              renderConnectorLocal?.({
                timelineItem: timelineDataItem,
                timelineItemAfter: timelineData[index + 1],
              })}
            {renderTimePointLocal?.({ timelineItem: timelineDataItem })}
          </TimelinePointContainer>
          <TimelineDetailsContainer role="presentation" aria-label="timeline details container">
            {renderDetailsLocal({ timelineItem: timelineDataItem })}
          </TimelineDetailsContainer>
        </TimelineRowItem>
      ))}
    </TimelineGraphStyled>
  );
}

TimelineGraph.TimePoint = TimePoint;
TimelineGraph.Connector = Connector;
TimelineGraph.DetailTitle = DetailTitle;
TimelineGraph.DetailValue = DetailValue;
TimelineGraph.Separator = TimeSeparator;
